import { ROOMS, BRAND_GREEN } from "../layout";
import { FurnitureSlot, ReceptionDeskPlaceholder, WaitingBenchPlaceholder } from "../Furniture";
import { DomeCameraPlaceholder, CubicleUnit } from "../Furniture";
import { SignDecal, EmergencyLight } from "../SignDecal";
import { FireExtinguisher, FirstAidBox } from "../Safety";
import { useOptionalTexture } from "../materials";
import { useMemo } from "react";
import { CanvasTexture, SRGBColorSpace, type Texture } from "three";

/**
 * Banco de 8 casilleros [NORMATIVO Anexo 60 1.1.1: almacenamiento de
 * artículos personales no permitidos], junto a recepción/verificación.
 */
export function Lockers({ position = [0, 0, 0] as [number, number, number], rotationY = 0 }) {
  return (
    <group name="casilleros" position={position} rotation={[0, rotationY, 0]}>
      {/* cuerpo */}
      <mesh position={[0, 0.95, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.6, 1.9, 0.45]} />
        <meshStandardMaterial color="#6b7a7d" roughness={0.55} metalness={0.35} />
      </mesh>
      {/* 8 puertas (4 × 2) con ranura y chapa */}
      {[-0.6, -0.2, 0.2, 0.6].flatMap((ox) =>
        [0.5, 1.4].map((oy) => (
          <group key={`${ox}-${oy}`} position={[ox, oy, 0.231]}>
            <mesh>
              <planeGeometry args={[0.36, 0.86]} />
              <meshStandardMaterial color="#7d8c8f" roughness={0.5} metalness={0.35} />
            </mesh>
            <mesh position={[0.12, 0, 0.004]}>
              <planeGeometry args={[0.05, 0.1]} />
              <meshStandardMaterial color="#3c4145" roughness={0.4} metalness={0.5} />
            </mesh>
            <mesh position={[0, 0.32, 0.004]}>
              <planeGeometry args={[0.24, 0.03]} />
              <meshStandardMaterial color="#4a5457" roughness={0.5} />
            </mesh>
          </group>
        )),
      )}
      <SignDecal text="CASILLEROS" position={[0, 2.15, 0.24]} variant="info" width={1.4} height={0.35} />
    </group>
  );
}

/**
 * Pantalla del módulo de examen en los monitores de los cubículos
 * [SIMULADO]: resumen de datos del aspirante que la plataforma despliega
 * para verificación antes de iniciar [NORMATIVO Anexo 60 num. 1.4.5].
 * Todos los datos son sintéticos.
 */
export function useAbstractScreenTexture(): Texture {
  return useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 320;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#10241f";
    ctx.fillRect(0, 0, 512, 320);
    ctx.fillStyle = "#1f7a7c";
    ctx.fillRect(0, 0, 512, 44);
    ctx.fillStyle = "#ffffff";
    ctx.font = "700 20px sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("PRUEBA TEÓRICA · DEMOSTRACIÓN", 18, 29);
    ctx.font = "600 16px sans-serif";
    ctx.fillStyle = "#d9a441";
    ctx.fillText("RESUMEN DEL ASPIRANTE — VERIFIQUE SUS DATOS", 18, 72);
    ctx.fillStyle = "#cfe3dd";
    ctx.font = "400 15px sans-serif";
    const rows = [
      "Documento:  CC 1••••••23  (sintético)",
      "Nombre:     ASPIRANTE DE DEMOSTRACIÓN",
      "Categoría:  B1 — automóvil",
      "Preguntas:  40   ·   Tiempo máximo: 70 min",
      "Aprobación: 80 % núcleos + 80 % actitudinal",
    ];
    rows.forEach((r, i) => ctx.fillText(r, 18, 108 + i * 30));
    ctx.fillStyle = "#8fb3ad";
    ctx.font = "italic 13px sans-serif";
    ctx.fillText("Datos sintéticos. Simulación educativa sin validez para trámites.", 18, 292);
    const tex = new CanvasTexture(canvas);
    tex.colorSpace = SRGBColorSpace;
    tex.anisotropy = 4;
    return tex;
  }, []);
}

/**
 * Recepción [NORMATIVO Res. 8150/2026]: mostrador de registro +
 * torre de autoregistro (kiosco) junto a la entrada.
 */
export function Reception() {
  const { x, z, w } = ROOMS.reception;
  const cx = x + w / 2;
  return (
    <group name="room-recepcion">
      <FurnitureSlot kind="recepcion" position={[cx, 0, z + 3.4]} rotationY={Math.PI}>
        <ReceptionDeskPlaceholder />
      </FurnitureSlot>
      {/* kiosco de autoregistro junto a la puerta del corredor */}
      <group position={[x + 8.6, 0, z + 0.9]} rotation={[0, Math.PI / 4, 0]}>
        <mesh position={[0, 0.8, 0]} castShadow>
          <boxGeometry args={[0.5, 1.6, 0.4]} />
          <meshStandardMaterial color="#5d6a6e" roughness={0.45} metalness={0.3} />
        </mesh>
        <mesh position={[0, 1.25, 0.21]} rotation={[-0.25, 0, 0]}>
          <boxGeometry args={[0.4, 0.5, 0.03]} />
          <meshStandardMaterial color="#141a1d" roughness={0.25} />
        </mesh>
      </group>
      {/* carteles institucionales enmarcados (muro occidental) */}
      {[
        { y: 1.7, zz: z + 1.6, t: "BIENVENIDO" },
        { y: 1.7, zz: z + 3.6, t: "REGLAS DE LA SIMULACIÓN" },
      ].map((p) => (
        <group key={p.t} position={[x + 0.08, p.y, p.zz]} rotation={[0, Math.PI / 2, 0]}>
          <mesh>
            <boxGeometry args={[1.1, 1.4, 0.04]} />
            <meshStandardMaterial color="#8d8676" roughness={0.4} metalness={0.3} />
          </mesh>
          <mesh position={[0, 0, 0.025]}>
            <planeGeometry args={[0.96, 1.26]} />
            <meshStandardMaterial color="#f0ede2" roughness={0.85} />
          </mesh>
          <mesh position={[0, 0.44, 0.03]}>
            <planeGeometry args={[0.8, 0.3]} />
            <meshStandardMaterial color="#2f6364" roughness={0.7} />
          </mesh>
        </group>
      ))}
      {/* fila de espera: marcadores de piso frente al mostrador */}
      {[0.8, 1.7, 2.6].map((dz) => (
        <mesh key={dz} rotation={[-Math.PI / 2, 0, 0]} position={[cx, 0.06, z + dz]}>
          <circleGeometry args={[0.22, 20]} />
          <meshStandardMaterial color="#2f6364" roughness={0.8} transparent opacity={0.75} />
        </mesh>
      ))}
      <QueuePosts position={[cx - 1.6, 0, z + 1.6]} rotationY={0.2} />
      <IndoorPlant position={[x + 0.7, 0, z + 4.6]} />
      <DomeCameraPlaceholder position={[cx, 3.12, z + 3]} />
      {/* casilleros junto a recepción/verificación + botiquín */}
      <Lockers position={[x + 0.5, 0, z + 2.6]} rotationY={Math.PI / 2} />
      <FirstAidBox position={[cx - 1.4, 1.5, z + 5.8]} />
      <SignDecal text="RECEPCIÓN" rotationY={Math.PI} position={[cx, 2.5, z + 5.85]} variant="info" width={1.6} height={0.4} />
    </group>
  );
}

/** Planta interior en matera (esquina de salas). */
export function IndoorPlant({ position = [0, 0, 0] as [number, number, number] }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.22, 0]} castShadow>
        <cylinderGeometry args={[0.24, 0.19, 0.44, 12]} />
        <meshStandardMaterial color="#8c5a3c" roughness={0.85} />
      </mesh>
      {[[0, 0.85, 0, 0.34], [0.14, 0.68, 0.08, 0.24], [-0.13, 0.66, -0.06, 0.22]].map(([x, y, z, r], i) => (
        <mesh key={i} position={[x, y, z]} castShadow>
          <icosahedronGeometry args={[r, 1]} />
          <meshStandardMaterial color={i ? "#4d7040" : "#567a42"} roughness={0.95} flatShading />
        </mesh>
      ))}
    </group>
  );
}

/** Postes de fila con banda (control de acceso al mostrador). */
export function QueuePosts({ position = [0, 0, 0] as [number, number, number], rotationY = 0 }) {
  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      {[-0.9, 0.9].map((x) => (
        <group key={x} position={[x, 0, 0]}>
          <mesh position={[0, 0.5, 0]} castShadow>
            <cylinderGeometry args={[0.035, 0.035, 1, 10]} />
            <meshStandardMaterial color="#3c4145" metalness={0.65} roughness={0.35} />
          </mesh>
          <mesh position={[0, 0.03, 0]}>
            <cylinderGeometry args={[0.17, 0.19, 0.06, 12]} />
            <meshStandardMaterial color="#3c4145" roughness={0.5} metalness={0.5} />
          </mesh>
        </group>
      ))}
      <mesh position={[0, 0.92, 0]}>
        <boxGeometry args={[1.8, 0.06, 0.015]} />
        <meshStandardMaterial color="#2f6364" roughness={0.7} />
      </mesh>
    </group>
  );
}

/** Sala de espera diferenciada de la sala de examen [NORMATIVO Anexo 60 1.1.1 b)]. */
export function WaitingRoom() {
  const { x, z, w } = ROOMS.waiting;
  const cx = x + w / 2;
  return (
    <group name="room-espera">
      <FurnitureSlot kind="sillas" position={[cx - 1.5, 0, z + 2]} rotationY={Math.PI}>
        <WaitingBenchPlaceholder />
      </FurnitureSlot>
      <FurnitureSlot kind="sillas" position={[cx - 1.5, 0, z + 4]} >
        <WaitingBenchPlaceholder />
      </FurnitureSlot>
      <IndoorPlant position={[x + w - 1, 0, z + 0.9]} />
      {/* dispensador de agua */}
      <group position={[x + w - 0.9, 0, z + 3.2]}>
        <mesh position={[0, 0.55, 0]} castShadow>
          <boxGeometry args={[0.36, 1.1, 0.36]} />
          <meshStandardMaterial color="#dfe3e4" roughness={0.4} />
        </mesh>
        <mesh position={[0, 1.25, 0]}>
          <cylinderGeometry args={[0.16, 0.16, 0.3, 12]} />
          <meshStandardMaterial color="#bcd8e4" roughness={0.15} transparent opacity={0.7} />
        </mesh>
      </group>
      {/* reloj de pared */}
      <group position={[cx, 2.35, z + 5.85]}>
        <mesh>
          <cylinderGeometry args={[0.22, 0.22, 0.04, 20]} />
          <meshStandardMaterial color="#f0ede2" roughness={0.6} />
        </mesh>
        <mesh position={[0, 0.05, 0.03]} rotation={[0, 0, -0.8]}>
          <boxGeometry args={[0.015, 0.14, 0.008]} />
          <meshStandardMaterial color="#3c4145" roughness={0.5} />
        </mesh>
        <mesh position={[0.04, 0, 0.03]} rotation={[0, 0, -1.9]}>
          <boxGeometry args={[0.015, 0.1, 0.008]} />
          <meshStandardMaterial color="#3c4145" roughness={0.5} />
        </mesh>
      </group>
      <SignDecal text="SALA DE ESPERA" rotationY={Math.PI} position={[cx, 2.5, z + 5.85]} variant="info" width={1.8} height={0.4} />
    </group>
  );
}

/** Cubículos privados de verificación documental y control de identidad. */
export function PrivateCubicles() {
  const { x, z } = ROOMS.cubicles;
  return (
    <group name="room-cubiculos">
      {[0, 1, 2].map((i) => (
        <CubicleUnit key={i} position={[x + 1.2 + i * 2.4, 0, z + 3]} rotationY={Math.PI} />
      ))}
      <DomeCameraPlaceholder position={[x + 4.5, 3.12, z + 3]} />
      <SignDecal text="VERIFICACIÓN" rotationY={Math.PI} position={[x + 4.5, 2.5, z + 5.85]} variant="info" width={1.8} height={0.4} />
    </group>
  );
}

/**
 * Sala teórica [NORMATIVO Anexo 60 1.1.1 a)]: 12 puestos separados,
 * 2 m² por aspirante, cámaras domo, paneles acústicos (NTC 6519-1),
 * luminarias de techo, zócalo y variación sutil entre puestos.
 */
export function TheoryRoom() {
  const { x, z } = ROOMS.theory;
  const acousticTex = useOptionalTexture("/assets/textures/acustico.png", [3, 1]);
  const screenTex = useAbstractScreenTexture();
  const desks: { pos: [number, number]; rot: number; lit: boolean }[] = [];
  for (let row = 0; row < 2; row++) {
    for (let col = 0; col < 6; col++) {
      const i = row * 6 + col;
      desks.push({
        pos: [x + 2.4 + col * 3.4, z + 4 + row * 4.2],
        // variación sutil de orientación entre puestos
        rot: ((i * 37) % 11) * 0.012 - 0.06,
        lit: true,
      });
    }
  }
  return (
    <group name="room-teorica">
      {desks.map((d, i) => (
        <CubicleUnit key={i} position={[d.pos[0], 0, d.pos[1]]} rotationY={d.rot} lit={d.lit} screenTexture={null} />
      ))}
      {/* pantalla del módulo del sistema en el muro norte, visible desde los
          cubículos: resumen del aspirante y reglas de la prueba [SIMULADO] */}
      <mesh position={[x + 20, 1.7, z + 0.1]}>
        <planeGeometry args={[4.2, 2.1]} />
        <meshBasicMaterial map={screenTex} toneMapped={false} />
      </mesh>
      {/* marco de la pantalla */}
      <mesh position={[x + 20, 1.7, z + 0.07]}>
        <planeGeometry args={[4.36, 2.24]} />
        <meshStandardMaterial color="#14181a" roughness={0.4} metalness={0.4} />
      </mesh>
      {/* cámaras domo: una por fila de cubículos */}
      <DomeCameraPlaceholder position={[x + 6, 3.12, z + 4]} />
      <DomeCameraPlaceholder position={[x + 16, 3.12, z + 4]} />
      <DomeCameraPlaceholder position={[x + 6, 3.12, z + 8.2]} />
      <DomeCameraPlaceholder position={[x + 16, 3.12, z + 8.2]} />
      {/* luminarias de techo emisivas (2 × 3) */}
      {[x + 6, x + 12, x + 18].flatMap((lx) =>
        [z + 4, z + 10].map((lz) => (
          <mesh key={`${lx}-${lz}`} position={[lx, 3.12, lz]}>
            <boxGeometry args={[1.2, 0.06, 0.4]} />
            <meshStandardMaterial color="#f2f0e8" emissive="#fff6e2" emissiveIntensity={1.6} />
          </mesh>
        )),
      )}
      {/* paneles acústicos en muros norte y laterales */}
      {[-8.2, -4.2, -0.2].map((oz) => (
        <mesh key={oz} position={[x + 0.06, 1.7, -oz]}>
          <boxGeometry args={[0.05, 2, 3.4]} />
          <meshStandardMaterial color="#c2bcb0" roughness={1} map={acousticTex ?? undefined} />
        </mesh>
      ))}
      {[6, 12, 18].map((ox) => (
        <mesh key={ox} position={[x + ox, 1.7, z + 0.06]}>
          <boxGeometry args={[3.4, 2, 0.05]} />
          <meshStandardMaterial color="#c2bcb0" roughness={1} map={acousticTex ?? undefined} />
        </mesh>
      ))}
      {/* zócalo perimetral */}
      {[
        { p: [x + 12, 0.06, z + 0.08] as const, s: [24, 0.12, 0.06] as const },
        { p: [x + 12, 0.06, z + 13.92] as const, s: [24, 0.12, 0.06] as const },
        { p: [x + 0.08, 0.06, z + 7] as const, s: [0.06, 0.12, 14] as const },
        { p: [x + 23.92, 0.06, z + 7] as const, s: [0.06, 0.12, 14] as const },
      ].map((b, i) => (
        <mesh key={i} position={[b.p[0], b.p[1], b.p[2]]}>
          <boxGeometry args={[b.s[0], b.s[1], b.s[2]]} />
          <meshStandardMaterial color="#8d8878" roughness={0.7} />
        </mesh>
      ))}
      {/* franja institucional a media altura (identidad sobria en los muros) */}
      {[
        { p: [x + 12, 1.12, z + 0.1] as const, s: [24, 0.16, 0.05] as const },
        { p: [x + 12, 1.12, z + 13.9] as const, s: [24, 0.16, 0.05] as const },
        { p: [x + 0.1, 1.12, z + 7] as const, s: [0.05, 0.16, 14] as const },
        { p: [x + 23.9, 1.12, z + 7] as const, s: [0.05, 0.16, 14] as const },
      ].map((b, i) => (
        <mesh key={`franja-${i}`} position={[b.p[0], b.p[1], b.p[2]]}>
          <boxGeometry args={[b.s[0], b.s[1], b.s[2]]} />
          <meshStandardMaterial color={BRAND_GREEN} roughness={0.65} />
        </mesh>
      ))}
      {/* luces interiores de la sala (refuerzo de exposición) */}
      <pointLight position={[x + 8, 2.9, z + 5]} intensity={16} distance={14} decay={2} color="#fdf6e8" />
      <pointLight position={[x + 16, 2.9, z + 9]} intensity={16} distance={14} decay={2} color="#fdf6e8" />
      <SignDecal text="SALA TEÓRICA" position={[x + 12, 2.5, z + 13.85]} variant="info" width={1.8} height={0.4} />
      <EmergencyLight position={[x + 12, 2.9, z + 13.9]} />
      {/* extintor escénico de la sala teórica [Dec. 1072/2015] */}
      <FireExtinguisher position={[-12.5, 0, -3.2]} />
    </group>
  );
}

/**
 * Puesto de supervisor al fondo de la sala teórica, elevado 0.15 m,
 * con visión total de los 12 puestos [NORMATIVO Anexo 60 1.4.5].
 */
export function SupervisorPost() {
  const { x, z, w } = ROOMS.supervisor;
  return (
    <group name="room-supervisor">
      <mesh position={[x + w / 2, 0.075, z + 1]} receiveShadow>
        <boxGeometry args={[w - 2, 0.15, 1.8]} />
        <meshStandardMaterial color="#b5af9f" roughness={0.8} />
      </mesh>
      <mesh position={[x + w / 2, 0.62, z + 1.3]} castShadow>
        <boxGeometry args={[2.4, 0.9, 0.7]} />
        <meshStandardMaterial color="#8d8676" roughness={0.5} />
      </mesh>
      <mesh position={[x + w / 2, 1.35, z + 1.3]} castShadow>
        <boxGeometry args={[0.6, 0.38, 0.03]} />
        <meshStandardMaterial color="#20262a" roughness={0.3} />
      </mesh>
      <SignDecal text="SUPERVISOR" position={[x + w / 2, 2.5, z + 0.2]} variant="plate" width={1.6} height={0.4} />
    </group>
  );
}

/** Cuarto de comunicaciones y custodia de datos [NORMATIVO Anexo 60 2.1 b)]. */
export function CommsRoom() {
  const { x, z } = ROOMS.comms;
  return (
    <group name="room-comms">
      {[0, 1].map((i) => (
        <mesh key={i} position={[x + 1 + i * 1.1, 1, z + 1.2]} castShadow>
          <boxGeometry args={[0.8, 2, 0.6]} />
          <meshStandardMaterial color="#39424a" roughness={0.35} metalness={0.5} />
        </mesh>
      ))}
      <mesh position={[x + 4.5, 0.5, z + 2.5]} castShadow>
        <boxGeometry args={[2.4, 1, 0.8]} />
        <meshStandardMaterial color="#8d8676" roughness={0.5} />
      </mesh>
      <SignDecal text="COMUNICACIONES" rotationY={Math.PI} position={[x + 4.5, 2.5, z + 5.85]} variant="info" width={2.2} height={0.4} />
    </group>
  );
}

/**
 * Sanitarios accesibles [NORMATIVO Ley 1618/2013, NTC 6047]:
 * cabina de 1.8 × 1.8 m con barra de apoyo.
 */
export function Sanitarios() {
  const { x, z } = ROOMS.sanitarios;
  return (
    <group name="room-sanitarios">
      <mesh position={[x + 1, 0.42, z + 1.2]} castShadow>
        <boxGeometry args={[0.55, 0.84, 0.65]} />
        <meshStandardMaterial color="#e4e1d6" roughness={0.35} />
      </mesh>
      {/* barra de apoyo */}
      <mesh position={[x + 1, 0.75, z + 0.75]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.02, 0.02, 0.8, 8]} />
        <meshStandardMaterial color="#9aa1a5" metalness={0.7} roughness={0.3} />
      </mesh>
      <mesh position={[x + 3, 0.55, z + 0.8]}>
        <cylinderGeometry args={[0.28, 0.24, 0.16, 14]} />
        <meshStandardMaterial color="#e4e1d6" roughness={0.3} />
      </mesh>
      <mesh position={[x + 3, 0.4, z + 0.8]}>
        <boxGeometry args={[0.3, 0.8, 0.3]} />
        <meshStandardMaterial color="#d6d2c6" roughness={0.5} />
      </mesh>
      <SignDecal text="SANITARIO ACCESIBLE" rotationY={Math.PI} position={[x + 2, 2.3, z + 2.9]} variant="info" width={2.2} height={0.4} />
    </group>
  );
}

/**
 * Circulación interior: corredor accesible recepción → sala → egreso,
 * con señalización de ruta de evacuación [NORMATIVO NSR-10 Título K].
 */
export function Circulation() {
  const { x, z, w, d } = ROOMS.corridor;
  return (
    <group name="room-circulacion">
      <SignDecal text="SALIDA →" position={[x + 2, 0.02, z + d / 2]} orientation="floor" variant="evac" width={1.4} height={0.5} />
      <SignDecal text="SALIDA →" position={[x + w - 4, 0.02, z + d / 2]} orientation="floor" variant="evac" width={1.4} height={0.5} />
      {/* placas SALIDA en altura sobre el corredor [NSR-10 Título K] */}
      <SignDecal text="SALIDA →" position={[x + 2, 2.4, z + 0.15]} variant="evac" width={1.2} height={0.4} />
      <SignDecal text="SALIDA →" position={[x + w - 4, 2.4, z + 0.15]} variant="evac" width={1.2} height={0.4} />
      <EmergencyLight position={[x + 1, 2.9, z + 0.1]} />
      <EmergencyLight position={[x + w - 1, 2.9, z + 0.1]} />
      {/* extintor escénico del corredor [Dec. 1072/2015] */}
      <FireExtinguisher position={[x + 8, 0, z + 0.3]} />
    </group>
  );
}
